/**
 * LaunchPage Component
 *
 * Full-screen application launcher that loads tenant applications in an iframe.
 * Uses a centralized bootstrap loader to handle secure token injection via PostMessage.
 *
 * Features:
 * - Full-screen iframe for running tenant applications
 * - Secure token passing via PostMessage (never in URL)
 * - Exit button to return to launcher
 * - Loading and error states
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { ArrowLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useLayoutProps } from "../hooks/useLayoutProps";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { UnifiedInboxDropdown } from "../features/inbox/components/UnifiedInboxDropdown";
import { useApplication } from "../features/applications/hooks/useApplications";
import { CDN_BASE_URL, RUNTIME_API_URL, ROUTES } from "../lib/constants";
import { getErrorMessage } from "../lib/utils/errors";
import { getTokenExpiry } from "../lib/utils/token";
import logoImage from "../assets/logo.png";

type LaunchState = "loading" | "waiting_for_loader" | "ready" | "error";

export const LaunchPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, tenant, user, logout, refreshAuth } = useAuth();
  const layoutProps = useLayoutProps();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeReadyRef = useRef(false);
  const [tokenSent, setTokenSent] = useState(false);

  const tenantId = tenant?.tenantId || "";

  // Extract launch query parameters to pass to the application
  const launchParams = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return Object.keys(params).length > 0 ? params : null;
  }, [searchParams]);

  // Fetch application details
  const { data: application, isLoading: isLoadingApp, error: appError } = useApplication(tenantId, applicationId || "");

  // Update document title with app name
  useDocumentTitle(
    isLoadingApp ? 'Loading...' : (application?.applicationName ?? null)
  );

  // Build the CDN URL for the target application
  const getTargetUrl = useCallback(() => {
    if (!application || !tenant || !application.currentDeploymentId) {
      return null;
    }

    // CDN path pattern: /tenants/{tenantId}/applications/{applicationId}/deployments/{deploymentId}/index.html
    const targetPath = `/tenants/${tenantId}/applications/${applicationId}/deployments/${application.currentDeploymentId}/index.html`;

    return targetPath;
  }, [application, tenant, tenantId, applicationId]);

  // Build the loader URL
  const getLoaderUrl = useCallback(() => {
    const targetPath = getTargetUrl();
    if (!targetPath) return null;

    const encodedTarget = encodeURIComponent(`${CDN_BASE_URL}${targetPath}`);
    return `${CDN_BASE_URL}/loader.html?target=${encodedTarget}`;
  }, [getTargetUrl]);

  // Handle PostMessage from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin - must be from CDN
      if (!CDN_BASE_URL || !event.origin.includes(new URL(CDN_BASE_URL).host)) {
        // In development, also allow localhost origins
        const isDevOrigin = event.origin.includes("localhost") || event.origin.includes("127.0.0.1");
        if (!isDevOrigin) {
          console.warn("[LaunchPage] Rejected message from unauthorized origin:", event.origin);
          return;
        }
      }

      // Handle message types
      if (!event.data || typeof event.data !== "object") {
        return;
      }

      if (event.data.type === "LOADER_READY") {
        console.log("[LaunchPage] Received LOADER_READY from iframe");
        iframeReadyRef.current = true;

        // Send auth token to loader via postMessage
        if (iframeRef.current?.contentWindow && token) {
          const cdnOrigin = new URL(CDN_BASE_URL).origin;

          const message = {
            type: "AUTH_TOKEN",
            accessToken: token,
            accessTokenExpiry: getTokenExpiry(token)?.getTime() ?? null,
            tenantId: tenant?.tenantId,
            userId: user?.userId,
            applicationId,
            runtimeApiUrl: RUNTIME_API_URL,
            launchParams,
          };
          console.log("[LaunchPage] Sending AUTH_TOKEN to iframe:", message);

          iframeRef.current.contentWindow.postMessage(message, cdnOrigin);

          console.log("[LaunchPage] Sent AUTH_TOKEN to iframe");
          setTokenSent(true);
        } else {
          console.error("[LaunchPage] Failed to send token - missing iframe or token");
        }
      }

      // Handle token refresh request from iframe
      if (event.data.type === "REQUEST_TOKEN_REFRESH") {
        console.log("[LaunchPage] Received REQUEST_TOKEN_REFRESH from iframe");
        refreshAuth().catch((error) => {
          console.error("[LaunchPage] Token refresh failed:", error);
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [applicationId, token, tenant, user, refreshAuth, launchParams]);

  // Push token updates to iframe when token changes (after initial send)
  useEffect(() => {
    if (iframeReadyRef.current && tokenSent && iframeRef.current?.contentWindow && token) {
      const cdnOrigin = new URL(CDN_BASE_URL).origin;

      iframeRef.current.contentWindow.postMessage(
        {
          type: "TOKEN_UPDATE",
          accessToken: token,
          accessTokenExpiry: getTokenExpiry(token)?.getTime() ?? null,
        },
        cdnOrigin
      );

      console.log("[LaunchPage] Sent TOKEN_UPDATE to iframe");
    }
  }, [token, tokenSent]);

  // Compute launch state based on application data (derived state, not useEffect)
  const { launchState, errorMessage } = useMemo((): { launchState: LaunchState; errorMessage: string } => {
    if (isLoadingApp) {
      return { launchState: "loading", errorMessage: "" };
    }
    if (appError) {
      return { launchState: "error", errorMessage: getErrorMessage(appError) };
    }
    if (application) {
      // Validate application can be launched
      if (!application.isActive) {
        return { launchState: "error", errorMessage: "This application is currently inactive and cannot be launched." };
      }
      if (!application.currentDeploymentId) {
        return { launchState: "error", errorMessage: "This application has no active deployment. Please deploy the application first." };
      }
      if (application.deploymentStatus !== "deployed") {
        return {
          launchState: "error",
          errorMessage: `Application deployment is in "${application.deploymentStatus}" state. Please wait for deployment to complete.`,
        };
      }
      // Application is ready to launch - check if we've sent token already
      return { launchState: tokenSent ? "ready" : "waiting_for_loader", errorMessage: "" };
    }
    return { launchState: "loading", errorMessage: "" };
  }, [isLoadingApp, appError, application, tokenSent]);

  // Handle exit back to launcher
  const handleExit = () => {
    navigate(ROUTES.LAUNCHER);
  };

  // Render loading state
  if (launchState === "loading") {
    return (
      <div className="flex h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <Spinner size="lg" />
            <p className="mt-4" style={{ color: 'var(--color-text-secondary)' }}>Loading application...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (launchState === "error") {
    return (
      <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <Header
          tenantCode={layoutProps.tenantCode}
          userName={layoutProps.userName}
          userEmail={layoutProps.userEmail}
          activeRole={layoutProps.activeRole}
          onLogout={logout}
          onProfileClick={layoutProps.onProfileClick}
          onSettingsClick={layoutProps.onSettingsClick}
          rightActions={<UnifiedInboxDropdown />}
          logo={
            <div className="flex items-center gap-2">
              <img src={logoImage} alt="Ketrics" className="h-8 w-auto" />
            </div>
          }
        />
        <main className="flex-1 overflow-y-auto">
          <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
            <Button variant="secondary" onClick={handleExit} className="mb-6">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Launcher
            </Button>

            <Alert variant="error" className="mb-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Unable to Launch Application</h3>
                  <p className="mt-1 text-sm">{errorMessage}</p>
                </div>
              </div>
            </Alert>

            {application && (
              <div className="card shadow-card rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>{application.applicationName}</h2>
                {application.description && <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>{application.description}</p>}
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt style={{ color: 'var(--color-text-muted)' }}>Status</dt>
                    <dd className="font-medium">{application.isActive ? "Active" : "Inactive"}</dd>
                  </div>
                  <div>
                    <dt style={{ color: 'var(--color-text-muted)' }}>Deployment Status</dt>
                    <dd className="font-medium capitalize">{application.deploymentStatus}</dd>
                  </div>
                  <div>
                    <dt style={{ color: 'var(--color-text-muted)' }}>Version</dt>
                    <dd className="font-medium">{application.version || "N/A"}</dd>
                  </div>
                  <div>
                    <dt style={{ color: 'var(--color-text-muted)' }}>Deployment ID</dt>
                    <dd className="font-medium font-mono text-xs">{application.currentDeploymentId || "None"}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Get loader URL
  const loaderUrl = getLoaderUrl();

  // Render launch view with iframe
  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Header with exit button */}
      <Header
        tenantCode={layoutProps.tenantCode}
        userName={layoutProps.userName}
        userEmail={layoutProps.userEmail}
        activeRole={layoutProps.activeRole}
        onLogout={logout}
        onProfileClick={layoutProps.onProfileClick}
        onSettingsClick={layoutProps.onSettingsClick}
        rightActions={<UnifiedInboxDropdown />}
        logo={
          <div className="flex items-center gap-2">
            <img src={logoImage} alt="Ketrics" className="h-8 w-auto" />
          </div>
        }
      >
        <div className="flex items-center gap-4">
          {/* Application name */}
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{application?.applicationName}</span>

          {/* Loading indicator */}
          {launchState === "waiting_for_loader" && (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              <Spinner size="sm" />
              <span>Connecting...</span>
            </div>
          )}

          {/* Exit button */}
          <Button variant="secondary" size="sm" onClick={handleExit}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Exit
          </Button>
        </div>
      </Header>

      {/* Iframe container */}
      <main className="flex-1 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-raised)' }}>
        {loaderUrl && (
          <iframe
            ref={iframeRef}
            src={loaderUrl}
            title={application?.applicationName || "Application"}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
            allow="clipboard-read; clipboard-write"
          />
        )}
      </main>
    </div>
  );
};

export default LaunchPage;
